// =============================================================================
// PAGES/PIPELINESBOARD.TSX — Board (só-leitura) do espelho de pipelines do Ploomes
// =============================================================================
// Item "Operações" da sidebar. Seletor de pipeline → colunas = estágios (cadência
// do Ploomes) → cartões das operações posicionadas (Fase 1+2). Busca por
// número/comprador e modal com a trilha do negócio pelos funis. Só-leitura:
// clicar no cartão abre a operação; não move estágio (o espelho reflete o Ploomes).
// =============================================================================
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Route as RouteIcon, X, Loader2, MapPin, User2, ArrowRightLeft, CheckSquare, Square, AlertTriangle, Plus, ChevronDown, Pencil, SlidersHorizontal, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { pipelinesApi, type PipelineBoard, type PipelineEstagioBoard, type PipelineCard, type TrilhaItem, type ChecklistGrupoView } from '../services/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import NovaOperacaoModal from '../components/NovaOperacaoModal';
import ConfirmModal from '../components/ConfirmModal';

const BRL = (n: number | null): string =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const ORIGEM_COR: Record<string, string> = {
  '5A': 'bg-indigo-100 text-indigo-700',
  S4: 'bg-amber-100 text-amber-700',
  VAR: 'bg-slate-100 text-slate-600',
};

const PAGE = 15;

interface ColState { cards: PipelineCard[]; total: number; page: number; carregando: boolean; }

export default function PipelinesBoard() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.papel === 'ADMIN';
  const [board, setBoard] = useState<PipelineBoard[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [colunas, setColunas] = useState<Record<string, ColState>>({});
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState<{ q: string; resp: ColState } | null>(null);
  const [trilha, setTrilha] = useState<{ card: PipelineCard; itens: TrilhaItem[] | null } | null>(null);
  const [gate, setGate] = useState<{ card: PipelineCard; estagioDestinoId: string; estagioDestinoNome: string; grupos: ChecklistGrupoView[]; avancando: boolean } | null>(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [novaOperacao, setNovaOperacao] = useState(false);
  const novoRef = useRef<HTMLDivElement>(null);
  const [menuEsteira, setMenuEsteira] = useState(false);
  const menuEsteiraRef = useRef<HTMLDivElement>(null);
  const [arquivarPrompt, setArquivarPrompt] = useState<{ pipeline: PipelineBoard; operacoes: number; historicos: number } | null>(null);
  const [acaoEsteira, setAcaoEsteira] = useState(false);
  const [confirmExcluir, setConfirmExcluir] = useState<PipelineBoard | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    if (!novoAberto && !menuEsteira) return;
    const h = (ev: MouseEvent) => {
      if (novoAberto && novoRef.current && !novoRef.current.contains(ev.target as Node)) setNovoAberto(false);
      if (menuEsteira && menuEsteiraRef.current && !menuEsteiraRef.current.contains(ev.target as Node)) setMenuEsteira(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [novoAberto, menuEsteira]);

  // Recarrega o board e re-seleciona a 1ª esteira (após excluir/arquivar)
  const recarregarESelecionar = useCallback(async () => {
    const b = await pipelinesApi.getBoard();
    setBoard(b);
    setPipelineId(b[0]?.id ?? null);
  }, []);

  // Ações de esteira (Editar/Duplicar/Deletar) — Admin
  const duplicarEsteira = useCallback(async (p: PipelineBoard) => {
    setMenuEsteira(false);
    try {
      const nova = await pipelinesApi.duplicarPipeline(p.id);
      toast.success(`Esteira duplicada: "${nova.nome}".`);
      await pipelinesApi.getBoard().then(setBoard).catch(() => {});
      navigate(`/esteiras/${nova.id}/editar`);
    } catch { toast.error('Falha ao duplicar a esteira.'); }
  }, [navigate]);

  // Deletar: abre o ConfirmModal (central, tom perigo) em vez de window.confirm
  const pedirExcluirEsteira = useCallback((p: PipelineBoard) => {
    setMenuEsteira(false);
    setConfirmExcluir(p);
  }, []);

  const confirmarExcluirEsteira = useCallback(async () => {
    const p = confirmExcluir;
    if (!p) return;
    setExcluindo(true);
    try {
      await pipelinesApi.excluirPipeline(p.id);
      toast.success('Esteira excluída.');
      setConfirmExcluir(null);
      await recarregarESelecionar();
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { erro?: string; operacoes?: number; historicos?: number } } }).response;
      setConfirmExcluir(null);
      if (resp?.status === 409 && resp.data?.erro === 'ESTEIRA_COM_OPERACOES') {
        setArquivarPrompt({ pipeline: p, operacoes: resp.data.operacoes ?? 0, historicos: resp.data.historicos ?? 0 });
      } else {
        toast.error('Não foi possível excluir a esteira.');
      }
    } finally { setExcluindo(false); }
  }, [confirmExcluir, recarregarESelecionar]);

  const arquivarEsteira = useCallback(async () => {
    if (!arquivarPrompt) return;
    setAcaoEsteira(true);
    try {
      await pipelinesApi.arquivarPipeline(arquivarPrompt.pipeline.id);
      toast.success('Esteira arquivada.');
      setArquivarPrompt(null);
      await recarregarESelecionar();
    } catch { toast.error('Falha ao arquivar a esteira.'); } finally { setAcaoEsteira(false); }
  }, [arquivarPrompt, recarregarESelecionar]);

  const recarregar = useCallback(() => {
    pipelinesApi.getBoard().then((b) => setBoard(b)).catch(() => {});
  }, []);

  // Carrega o skeleton (pipelines + estágios + contagens)
  useEffect(() => {
    pipelinesApi.getBoard()
      .then((b) => { setBoard(b); setPipelineId((atual) => atual ?? b[0]?.id ?? null); })
      .catch(() => setErro('Não foi possível carregar os pipelines.'));
  }, []);

  // Atualiza o board quando a sincronização (manual/auto) traz novidades
  useEffect(() => {
    const h = () => recarregar();
    window.addEventListener('operacoes:atualizar', h);
    return () => window.removeEventListener('operacoes:atualizar', h);
  }, [recarregar]);

  const pipeline = useMemo(() => board?.find((p) => p.id === pipelineId) ?? null, [board, pipelineId]);

  // Ao trocar de pipeline: carrega 1ª página dos estágios com operações
  useEffect(() => {
    if (!pipeline) return;
    setColunas({});
    setBusca(''); setBuscaAtiva(null);
    const comOps = pipeline.estagios.filter((e) => e.totalOperacoes > 0);
    comOps.forEach((e) => {
      setColunas((c) => ({ ...c, [e.id]: { cards: [], total: e.totalOperacoes, page: 0, carregando: true } }));
      pipelinesApi.getCards(pipeline.id, { estagioId: e.id, page: 1, pageSize: PAGE })
        .then((r) => setColunas((c) => ({ ...c, [e.id]: { cards: r.cards, total: r.total, page: 1, carregando: false } })))
        .catch(() => setColunas((c) => ({ ...c, [e.id]: { cards: [], total: e.totalOperacoes, page: 0, carregando: false } })));
    });
  }, [pipeline]);

  const carregarMais = useCallback((estagioId: string) => {
    if (!pipeline) return;
    const col = colunas[estagioId]; if (!col || col.carregando) return;
    setColunas((c) => ({ ...c, [estagioId]: { ...col, carregando: true } }));
    pipelinesApi.getCards(pipeline.id, { estagioId, page: col.page + 1, pageSize: PAGE })
      .then((r) => setColunas((c) => ({ ...c, [estagioId]: { cards: [...col.cards, ...r.cards], total: r.total, page: col.page + 1, carregando: false } })))
      .catch(() => setColunas((c) => ({ ...c, [estagioId]: { ...col, carregando: false } })));
  }, [pipeline, colunas]);

  const fazerBusca = useCallback(() => {
    const q = busca.trim();
    if (!pipeline || !q) { setBuscaAtiva(null); return; }
    setBuscaAtiva({ q, resp: { cards: [], total: 0, page: 0, carregando: true } });
    pipelinesApi.getCards(pipeline.id, { q, page: 1, pageSize: 50 })
      .then((r) => setBuscaAtiva({ q, resp: { cards: r.cards, total: r.total, page: 1, carregando: false } }))
      .catch(() => setBuscaAtiva({ q, resp: { cards: [], total: 0, page: 0, carregando: false } }));
  }, [busca, pipeline]);

  const abrirTrilha = useCallback((card: PipelineCard) => {
    setTrilha({ card, itens: null });
    pipelinesApi.getTrilha(card.id)
      .then((itens) => setTrilha({ card, itens }))
      .catch(() => setTrilha({ card, itens: [] }));
  }, []);

  // Mover operação de etapa — gate do checklist (estilo Ploomes)
  const moverCard = useCallback(async (card: PipelineCard, estagio: PipelineEstagioBoard) => {
    try {
      await pipelinesApi.mover(card.id, estagio.id);
      toast.success(`Movida para "${estagio.nome}".`);
      recarregar();
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { erro?: string; pendentes?: ChecklistGrupoView[] } } }).response;
      if (resp?.status === 409 && resp.data?.erro === 'CHECKLIST_PENDENTE') {
        setGate({ card, estagioDestinoId: estagio.id, estagioDestinoNome: estagio.nome, grupos: resp.data.pendentes ?? [], avancando: false });
      } else {
        toast.error('Não foi possível mover a operação.');
      }
    }
  }, [recarregar]);

  const marcarGateItem = useCallback(async (itemId: string, cumprido: boolean) => {
    if (!gate) return;
    try {
      await pipelinesApi.marcarChecklistItem(gate.card.id, itemId, cumprido, undefined, gate.card.estagioId ?? undefined);
      setGate((g) => g ? { ...g, grupos: g.grupos.map((gr) => ({ ...gr, itens: gr.itens.map((i) => i.id === itemId ? { ...i, cumprido } : i) })) } : g);
    } catch { toast.error('Não foi possível atualizar o item.'); }
  }, [gate]);

  const avancarAposGate = useCallback(async () => {
    if (!gate) return;
    setGate((g) => g ? { ...g, avancando: true } : g);
    try {
      await pipelinesApi.mover(gate.card.id, gate.estagioDestinoId);
      toast.success(`Movida para "${gate.estagioDestinoNome}".`);
      setGate(null);
      recarregar();
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { pendentes?: ChecklistGrupoView[] } } }).response;
      if (resp?.status === 409) {
        setGate((g) => g ? { ...g, grupos: resp.data?.pendentes ?? g.grupos, avancando: false } : g);
        toast.error('Ainda há itens obrigatórios pendentes.');
      } else { setGate((g) => g ? { ...g, avancando: false } : g); toast.error('Não foi possível avançar.'); }
    }
  }, [gate, recarregar]);

  if (erro) return <div className="p-6 text-sm text-red-600">{erro}</div>;
  if (!board) return <div className="p-6 flex items-center gap-2 text-sm text-[#5F6470]"><Loader2 className="w-4 h-4 animate-spin" /> Carregando pipelines…</div>;

  return (
    <div className="flex flex-col h-full">
      {/* Cabeçalho + seletor de pipeline */}
      <div className="px-5 pt-4 pb-2 shrink-0">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h1 className="text-lg font-semibold text-[#1A1D23]">Operações</h1>
          {isAdmin && (
            <div className="relative" ref={novoRef}>
              <button
                onClick={() => setNovoAberto((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-otm-navy px-3 py-1.5 rounded-lg hover:bg-otm-navy/90"
              >
                <Plus className="w-4 h-4" /> Novo <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {novoAberto && (
                <div className="absolute right-0 top-[115%] z-40 w-56 bg-white rounded-lg border border-[#E2E6ED] shadow-xl p-1">
                  <button
                    onClick={() => { setNovoAberto(false); navigate('/esteiras/novo'); }}
                    className="w-full flex items-center gap-2 text-left text-[13px] text-[#3A3F4A] px-3 py-2 rounded hover:bg-[#F2F4F8]"
                  >
                    <SlidersHorizontal className="w-4 h-4 text-[#9AA0AC]" /> Criar nova esteira
                  </button>
                  <button
                    onClick={() => { setNovoAberto(false); setNovaOperacao(true); }}
                    className="w-full flex items-center gap-2 text-left text-[13px] text-[#3A3F4A] px-3 py-2 rounded hover:bg-[#F2F4F8]"
                  >
                    <Plus className="w-4 h-4 text-[#9AA0AC]" /> Criar nova operação
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {board.map((p) => (
            <div
              key={p.id}
              className={`inline-flex items-center rounded-full text-[13px] font-medium border transition-colors ${
                p.id === pipelineId ? 'bg-otm-navy text-white border-otm-navy' : 'bg-white text-[#5F6470] border-[#E2E6ED] hover:bg-[#FAFBFC]'
              }`}
            >
              <button onClick={() => setPipelineId(p.id)} className="pl-3 py-1.5 pr-1">
                {p.nome}
                <span className={`ml-2 text-[11px] px-1.5 py-[1px] rounded-full ${p.id === pipelineId ? 'bg-white/20' : 'bg-[#EAECF2] text-[#5F6470]'}`}>{p.totalOperacoes}</span>
              </button>
              {isAdmin && p.id === pipelineId && (
                <div className="relative" ref={menuEsteiraRef}>
                  <button
                    onClick={() => setMenuEsteira((v) => !v)}
                    title="Opções da esteira"
                    className="pr-2.5 pl-1 py-1.5 text-white/80 hover:text-white"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {menuEsteira && (
                    <div className="absolute right-0 top-[120%] z-40 w-44 bg-white rounded-lg border border-[#E2E6ED] shadow-xl p-1">
                      <button onClick={() => { setMenuEsteira(false); navigate(`/esteiras/${p.id}/editar`); }} className="w-full flex items-center gap-2 text-left text-[13px] text-[#3A3F4A] px-3 py-2 rounded hover:bg-[#F2F4F8]"><Pencil className="w-4 h-4 text-[#9AA0AC]" /> Editar</button>
                      <button onClick={() => duplicarEsteira(p)} className="w-full flex items-center gap-2 text-left text-[13px] text-[#3A3F4A] px-3 py-2 rounded hover:bg-[#F2F4F8]"><Copy className="w-4 h-4 text-[#9AA0AC]" /> Duplicar</button>
                      <button onClick={() => pedirExcluirEsteira(p)} className="w-full flex items-center gap-2 text-left text-[13px] text-red-600 px-3 py-2 rounded hover:bg-red-50"><Trash2 className="w-4 h-4" /> Deletar</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Busca */}
      <div className="px-5 py-2 shrink-0">
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9AA0AC]" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fazerBusca(); if (e.key === 'Escape') { setBusca(''); setBuscaAtiva(null); } }}
            placeholder="Buscar por número ou comprador…"
            className="w-full pl-8 pr-8 py-1.5 text-[13px] rounded-lg border border-[#E2E6ED] focus:outline-none focus:ring-2 focus:ring-otm-navy/20"
          />
          {busca && (
            <button onClick={() => { setBusca(''); setBuscaAtiva(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9AA0AC] hover:text-[#5F6470]"><X className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      {buscaAtiva ? (
        <div className="flex-1 overflow-auto px-5 pb-5">
          <p className="text-xs text-[#5F6470] mb-2">{buscaAtiva.resp.carregando ? 'Buscando…' : `${buscaAtiva.resp.total} resultado(s) para “${buscaAtiva.q}”`}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {buscaAtiva.resp.cards.map((card) => (
              <Card key={card.id} card={card} estagioNome={estagioNome(pipeline, card.estagioId)} estagios={pipeline?.estagios} onOpen={() => navigate(`/operacoes/${card.id}`)} onTrilha={() => abrirTrilha(card)} onMover={(e) => moverCard(card, e)} onEditar={() => navigate(`/operacoes/${card.id}`)} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden px-5 pb-5">
          <div className="flex gap-3 h-full min-h-0">
            {pipeline?.estagios.map((e) => (
              <div key={e.id} className="flex flex-col bg-white rounded-[8px] border border-[#E2E6ED] w-72 shrink-0 shadow-sm h-full overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-[#E2E6ED]">
                  <h2 className="font-medium text-[13px] text-[#1A1D23] truncate" title={e.nome}>{e.nome}</h2>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {e.ultimo && <span className="text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-[1px] rounded-full">ganho</span>}
                    <span className="text-[11px] font-medium text-[#5F6470] bg-[#EAECF2] px-2 py-[2px] rounded-full">{e.totalOperacoes}</span>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-[#F7F8FA]">
                  {(colunas[e.id]?.cards ?? []).map((card) => (
                    <Card key={card.id} card={card} estagios={pipeline?.estagios} onOpen={() => navigate(`/operacoes/${card.id}`)} onTrilha={() => abrirTrilha(card)} onMover={(e) => moverCard(card, e)} onEditar={() => navigate(`/operacoes/${card.id}`)} />
                  ))}
                  {colunas[e.id]?.carregando && <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#9AA0AC]" /></div>}
                  {colunas[e.id] && !colunas[e.id].carregando && colunas[e.id].cards.length < colunas[e.id].total && (
                    <button onClick={() => carregarMais(e.id)} className="w-full text-[12px] text-otm-navy font-medium py-1.5 rounded-lg hover:bg-otm-navy/5">
                      Carregar mais ({colunas[e.id].total - colunas[e.id].cards.length})
                    </button>
                  )}
                  {e.totalOperacoes === 0 && <p className="text-[12px] text-[#9AA0AC] text-center py-3">—</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de trilha */}
      {trilha && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setTrilha(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E6ED]">
              <div>
                <h3 className="font-semibold text-[#1A1D23] text-sm">Trilha pelos funis</h3>
                <p className="text-[12px] text-[#5F6470]">{trilha.card.numero} · {trilha.card.comprador}</p>
              </div>
              <button onClick={() => setTrilha(null)} className="text-[#9AA0AC] hover:text-[#5F6470]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {trilha.itens == null ? (
                <div className="flex items-center gap-2 text-sm text-[#5F6470]"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
              ) : trilha.itens.length === 0 ? (
                <p className="text-sm text-[#5F6470]">Sem trilha registrada para esta operação.</p>
              ) : (
                <ol className="space-y-3">
                  {trilha.itens.map((it, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center text-[11px] font-semibold shrink-0">{i + 1}</div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-[#1A1D23]">{it.pipeline}</p>
                        <p className="text-[12px] text-[#5F6470]">{it.estagio ?? '—'}
                          {it.status && <span className={`ml-2 text-[10px] px-1.5 py-[1px] rounded-full ${it.status === 'ganho' ? 'bg-green-100 text-green-700' : it.status === 'perdido' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{it.status}</span>}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal central do gate de checklist (estilo Ploomes) */}
      {gate && (() => {
        const faltam = gate.grupos.reduce((n, g) => n + g.itens.filter((i) => i.obrigatorio && !i.cumprido).length, 0);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setGate(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between px-5 py-3 border-b border-[#E2E6ED]">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#1A1D23] text-sm">Itens pendentes para avançar</h3>
                    <p className="text-[12px] text-[#5F6470]">{gate.card.numero} · {gate.card.comprador} → <span className="font-medium">{gate.estagioDestinoNome}</span></p>
                  </div>
                </div>
                <button onClick={() => setGate(null)} className="text-[#9AA0AC] hover:text-[#5F6470]"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5">
                <p className="text-[13px] text-[#5F6470] mb-3">Conclua os itens obrigatórios da etapa atual antes de avançar. Marque abaixo o que já foi cumprido.</p>
                <div className="space-y-3">
                  {gate.grupos.map((g) => (
                    <div key={g.id}>
                      <p className="text-[11px] font-semibold text-[#5F6470] mb-1" title={g.textoInfo ?? undefined}>{g.nome}</p>
                      <ul className="space-y-1.5">
                        {g.itens.map((it) => (
                          <li key={it.id}>
                            <button
                              onClick={() => marcarGateItem(it.id, !it.cumprido)}
                              className="w-full flex items-center gap-2 text-left text-[13px] border border-[#EEF0F4] rounded-lg px-3 py-2 hover:bg-[#F7F8FA]"
                            >
                              {it.cumprido ? <CheckSquare className="w-4 h-4 text-green-600 shrink-0" /> : <Square className="w-4 h-4 text-[#9AA0AC] shrink-0" />}
                              <span className={`flex-1 ${it.cumprido ? 'text-[#9AA0AC] line-through' : 'text-[#3A3F4A]'}`}>{it.nome}</span>
                              {it.obrigatorio && <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-[1px] rounded-full shrink-0">obrigatório</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2 mt-4">
                  <span className="text-[12px] text-[#5F6470]">{faltam === 0 ? 'Tudo pronto.' : `${faltam} item(ns) obrigatório(s) pendente(s).`}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setGate(null)} className="text-[13px] text-[#5F6470] px-3 py-1.5 rounded-lg hover:bg-[#F2F4F8]">Cancelar</button>
                    <button
                      onClick={avancarAposGate}
                      disabled={faltam > 0 || gate.avancando}
                      className="text-[13px] font-semibold text-white bg-otm-navy px-4 py-1.5 rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                      {gate.avancando && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Avançar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirmar exclusão de esteira (modal central, substitui window.confirm) */}
      <ConfirmModal
        aberto={!!confirmExcluir}
        titulo="Excluir esteira"
        tom="perigo"
        processando={excluindo}
        textoConfirmar="Excluir"
        mensagem={<>Tem certeza que deseja excluir a esteira <strong>{confirmExcluir?.nome}</strong>? Esta ação é definitiva. Se houver operações vinculadas, você poderá arquivá-la em vez de excluir.</>}
        onConfirmar={confirmarExcluirEsteira}
        onCancelar={() => { if (!excluindo) setConfirmExcluir(null); }}
      />

      {/* Esteira com operações → oferecer Arquivar em vez de excluir (preserva dados) */}
      {arquivarPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setArquivarPrompt(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-2 px-5 py-3 border-b border-[#E2E6ED]">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-[#1A1D23] text-sm">Esteira com operações</h3>
                <p className="text-[12px] text-[#5F6470]">{arquivarPrompt.pipeline.nome}</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-[13px] text-[#3A3F4A]">
                Esta esteira tem <strong>{arquivarPrompt.operacoes}</strong> operação(ões){arquivarPrompt.historicos > 0 ? <> e <strong>{arquivarPrompt.historicos}</strong> registro(s) de trilha</> : null} vinculada(s). Para preservar os dados, ela não é excluída.
              </p>
              <p className="text-[13px] text-[#5F6470] mt-2">Você pode <strong>arquivar</strong>: some do quadro, mas as operações e o histórico ficam intactos (reversível).</p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setArquivarPrompt(null)} className="text-[13px] text-[#5F6470] px-3 py-1.5 rounded-lg hover:bg-[#F2F4F8]">Cancelar</button>
                <button onClick={arquivarEsteira} disabled={acaoEsteira} className="text-[13px] font-semibold text-white bg-otm-navy px-4 py-1.5 rounded-lg disabled:opacity-40 inline-flex items-center gap-1.5">
                  {acaoEsteira && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Arquivar esteira
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Criar nova operação (Admin → dropdown "Novo") */}
      {novaOperacao && (
        <NovaOperacaoModal
          onClose={() => setNovaOperacao(false)}
          onCriada={(op) => {
            setNovaOperacao(false);
            toast.success('Operação criada.');
            recarregar();
            navigate(`/operacoes/${op.id}`);
          }}
          onImportadoSemCcv={(id) => { setNovaOperacao(false); navigate(`/operacoes/${id}`); }}
        />
      )}
    </div>
  );
}

function estagioNome(pipeline: PipelineBoard | null, estagioId: string | null): string | undefined {
  if (!pipeline || !estagioId) return undefined;
  return pipeline.estagios.find((e) => e.id === estagioId)?.nome;
}

function Card({ card, estagioNome, estagios, onOpen, onTrilha, onMover, onEditar }: { card: PipelineCard; estagioNome?: string; estagios?: PipelineEstagioBoard[]; onOpen: () => void; onTrilha: () => void; onMover?: (e: PipelineEstagioBoard) => void; onEditar?: () => void }) {
  const [moverAberto, setMoverAberto] = useState(false);
  const destinos = (estagios ?? []).filter((e) => e.id !== card.estagioId);
  return (
    <div className="bg-white rounded-lg border border-[#E2E6ED] p-3 shadow-sm hover:shadow transition-shadow cursor-pointer group" onClick={onOpen}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-[#1A1D23]">{card.numero}</span>
        <div className="flex items-center gap-1 shrink-0">
          <span className={`text-[10px] font-medium px-1.5 py-[1px] rounded-full ${ORIGEM_COR[card.origem] ?? 'bg-slate-100 text-slate-600'}`}>{card.origem}</span>
          {onMover && destinos.length > 0 && (
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setMoverAberto((v) => !v); }}
                title="Mover de etapa"
                className="text-[#9AA0AC] hover:text-otm-navy opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
              </button>
              {moverAberto && (
                <div className="absolute right-0 top-[115%] z-30 w-56 bg-white rounded-lg border border-[#E2E6ED] shadow-xl p-1" onClick={(e) => e.stopPropagation()}>
                  <p className="text-[10px] font-semibold text-[#9AA0AC] px-2 py-1">Mover para…</p>
                  <ul className="max-h-60 overflow-y-auto">
                    {destinos.map((e) => (
                      <li key={e.id}>
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setMoverAberto(false); onMover(e); }}
                          className="w-full text-left text-[12px] text-[#3A3F4A] px-2 py-1.5 rounded hover:bg-[#F2F4F8] truncate"
                        >
                          {e.nome}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onTrilha(); }}
            title="Ver trilha pelos funis"
            className="text-[#9AA0AC] hover:text-otm-navy opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <RouteIcon className="w-3.5 h-3.5" />
          </button>
          {onEditar && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditar(); }}
              title="Editar operação"
              className="text-[#9AA0AC] hover:text-otm-navy opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <p className="text-[13px] text-[#1A1D23] mt-1 truncate" title={card.comprador}>{card.comprador}</p>
      {estagioNome && <p className="text-[11px] text-[#9AA0AC] truncate">{estagioNome}</p>}
      <div className="flex items-center gap-3 mt-2 text-[11px] text-[#5F6470]">
        <span className="font-medium">{BRL(card.valorFinanciamento)}</span>
        {card.cidadeImovel && <span className="flex items-center gap-0.5 truncate"><MapPin className="w-3 h-3" />{card.cidadeImovel}</span>}
      </div>
      {card.responsavel && <p className="flex items-center gap-1 text-[11px] text-[#9AA0AC] mt-1"><User2 className="w-3 h-3" />{card.responsavel}</p>}
      <div className="flex items-center gap-1.5 mt-1.5">
        <FarolDocs aplicaveis={card.docsAplicaveis} pendentes={card.docsPendentes} />
        {card.status === 'CONGELADA' && <span className="inline-block text-[10px] text-blue-700 bg-blue-100 px-1.5 py-[1px] rounded-full">congelada</span>}
      </div>
    </div>
  );
}

// Farol de documentação pendente (checklist documental SPEC_D). Verde=completo,
// âmbar=parcial, vermelho=muito pendente; neutro quando ainda não há checklist resolvido.
function FarolDocs({ aplicaveis, pendentes }: { aplicaveis: number; pendentes: number }) {
  if (!aplicaveis) return <span className="text-[10px] text-[#9AA0AC] bg-[#F2F4F8] px-1.5 py-[1px] rounded-full">docs —</span>;
  if (pendentes === 0) return <span className="text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-[1px] rounded-full">docs ok</span>;
  const ratio = pendentes / aplicaveis;
  const cls = ratio < 0.34 ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100';
  return <span className={`text-[10px] font-medium px-1.5 py-[1px] rounded-full ${cls}`} title={`${pendentes} de ${aplicaveis} documentos pendentes`}>{pendentes} doc{pendentes > 1 ? 's' : ''} pend.</span>;
}
